import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

const AppVersionSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeApp, setActiveApp] = useState('userApp');

  const theme = {
    primaryColor: '#FF6961',
    primaryDark: '#E6524A',
    textPrimary: '#0F172A',
    textSecondary: '#64748B',
    backgroundColor: '#F8FAFC'
  };

  const appTypes = [
    { id: 'userApp', label: 'User App', icon: '👤', description: 'Customer-facing food ordering app' },
    { id: 'restaurantApp', label: 'Restaurant App', icon: '🍽️', description: 'Restaurant partner management app' },
    { id: 'deliveryApp', label: 'Delivery App', icon: '🚴', description: 'Delivery partner app' }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getAppVersionSettings();
      const raw = response.data?.data || response.data || {};
      // The API nests the per-app config under `appVersions`, but the UI keys
      // off userApp/restaurantApp/deliveryApp directly — unwrap that layer so
      // settings['userApp'] resolves (otherwise fields render empty and saves
      // look like no-ops).
      setSettings(raw.appVersions || raw);
      setMessage({ type: '', text: '' });
    } catch (error) {
      console.error('Failed to fetch app version settings:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to fetch app version settings'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (appType, field, value) => {
    setSettings(prev => ({
      ...prev,
      [appType]: {
        ...prev[appType],
        [field]: value
      }
    }));
  };

  const handleToggle = (appType, field) => {
    setSettings(prev => ({
      ...prev,
      [appType]: {
        ...prev[appType],
        [field]: !prev[appType]?.[field]
      }
    }));
  };

  const handleSave = async (appType) => {
    setSaving(prev => ({ ...prev, [appType]: true }));
    setMessage({ type: '', text: '' });

    try {
      const appSettings = settings[appType];
      const apiAppType = appType.replace('App', ''); // userApp -> user

      await settingsAPI.updateAppVersionSettings(apiAppType, appSettings);
      await fetchSettings();
      setMessage({ type: 'success', text: `${getAppLabel(appType)} settings updated successfully!` });

      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to update app version settings'
      });
    } finally {
      setSaving(prev => ({ ...prev, [appType]: false }));
    }
  };

  const getAppLabel = (appType) => {
    const app = appTypes.find(a => a.id === appType);
    return app ? app.label : appType;
  };

  const validateVersion = (version) => {
    // Accept semver with an OPTIONAL Flutter build number: 1.0.30 or 1.0.30+55.
    // Include the +buildNumber to force-update between builds that share a semver.
    const versionRegex = /^\d+\.\d+\.\d+(\+\d+)?$/;
    return versionRegex.test(version);
  };

  if (loading) {
    return (
      <div style={styles.layout}>
        <Navbar />
        <div style={styles.content}>
          <div style={styles.loading}>Loading app version settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.layout}>
      <Navbar />
      <div style={styles.content}>
        <div style={styles.header}>
          <h1 style={styles.title}>App Version Management</h1>
          <p style={styles.subtitle}>Control force updates for all mobile apps</p>
        </div>

        {message.text && (
          <div style={{
            ...styles.message,
            ...(message.type === 'success' ? styles.messageSuccess : styles.messageError)
          }}>
            {message.text}
          </div>
        )}

        <div style={styles.container}>
          {/* App Selection Tabs */}
          <div style={styles.tabContainer}>
            {appTypes.map(app => (
              <button
                key={app.id}
                onClick={() => setActiveApp(app.id)}
                style={{
                  ...styles.tab,
                  ...(activeApp === app.id ? styles.tabActive : {})
                }}
              >
                <span style={styles.tabIcon}>{app.icon}</span>
                <span style={styles.tabLabel}>{app.label}</span>
              </button>
            ))}
          </div>

          {/* Active App Settings */}
          <div style={styles.tabContent}>
            {appTypes.map(app => (
              activeApp === app.id && (
                <div key={app.id} style={styles.section}>
                  <div style={styles.appHeader}>
                    <div style={styles.appInfo}>
                      <span style={styles.appIcon}>{app.icon}</span>
                      <div>
                        <h2 style={styles.sectionTitle}>{app.label}</h2>
                        <p style={styles.appDescription}>{app.description}</p>
                      </div>
                    </div>
                    <div style={styles.statusBadge}>
                      <span style={{
                        ...styles.badge,
                        ...(settings[app.id]?.enabled ? styles.badgeActive : styles.badgeInactive)
                      }}>
                        {settings[app.id]?.enabled ? 'Force Update Enabled' : 'Force Update Disabled'}
                      </span>
                    </div>
                  </div>

                  {/* Enable/Disable Toggle */}
                  <div style={styles.toggleSection}>
                    <div style={styles.toggleInfo}>
                      <strong>Enable Force Update</strong>
                      <p style={styles.toggleDescription}>
                        When enabled, users with app versions below the minimum will be required to update before using the app.
                      </p>
                    </div>
                    <label style={styles.switch}>
                      <input
                        type="checkbox"
                        checked={settings[app.id]?.enabled || false}
                        onChange={() => handleToggle(app.id, 'enabled')}
                        style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
                      />
                      {/* Self-contained toggle: color + knob are driven by state
                          via INLINE styles so it always reflects on/off (the old
                          version depended on a CSS class that was never applied,
                          leaving the switch visually dead). */}
                      <span style={{
                        ...styles.slider,
                        backgroundColor: settings[app.id]?.enabled ? theme.primaryColor : '#CBD5E1',
                      }}>
                        <span style={{
                          position: 'absolute',
                          height: '20px',
                          width: '20px',
                          top: '4px',
                          left: settings[app.id]?.enabled ? '26px' : '4px',
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          transition: 'left 0.2s',
                        }} />
                      </span>
                    </label>
                  </div>

                  {/* Version Settings */}
                  <div style={styles.versionSection}>
                    <h3 style={styles.subsectionTitle}>Version Settings</h3>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Current Version *</label>
                        <input
                          type="text"
                          value={settings[app.id]?.currentVersion || ''}
                          onChange={(e) => handleInputChange(app.id, 'currentVersion', e.target.value)}
                          style={{
                            ...styles.input,
                            ...(settings[app.id]?.currentVersion && !validateVersion(settings[app.id]?.currentVersion) ? styles.inputError : {})
                          }}
                          placeholder="1.0.30+55"
                        />
                        <small style={styles.helpText}>Latest store version, WITH the build number — e.g. 1.0.30+55 (matches pubspec version)</small>
                      </div>

                      <div style={styles.formGroup}>
                        <label style={styles.label}>Minimum Required Version *</label>
                        <input
                          type="text"
                          value={settings[app.id]?.minimumVersion || ''}
                          onChange={(e) => handleInputChange(app.id, 'minimumVersion', e.target.value)}
                          style={{
                            ...styles.input,
                            ...(settings[app.id]?.minimumVersion && !validateVersion(settings[app.id]?.minimumVersion) ? styles.inputError : {})
                          }}
                          placeholder="1.0.30+55"
                        />
                        <small style={styles.helpText}>Include the +buildNumber (e.g. 1.0.30+55). Apps below this build are forced to update. Only set a build-specific minimum once a build that reports its build number is live on the store.</small>
                      </div>
                    </div>
                  </div>

                  {/* Store URLs */}
                  <div style={styles.storeSection}>
                    <h3 style={styles.subsectionTitle}>Store URLs</h3>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>
                        <span style={styles.platformIcon}>🍎</span> iOS App Store URL
                      </label>
                      <input
                        type="url"
                        value={settings[app.id]?.iosStoreUrl || ''}
                        onChange={(e) => handleInputChange(app.id, 'iosStoreUrl', e.target.value)}
                        style={styles.input}
                        placeholder="https://apps.apple.com/app/id..."
                      />
                      <small style={styles.helpText}>Link to the app on Apple App Store</small>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>
                        <span style={styles.platformIcon}>🤖</span> Android Play Store URL
                      </label>
                      <input
                        type="url"
                        value={settings[app.id]?.androidStoreUrl || ''}
                        onChange={(e) => handleInputChange(app.id, 'androidStoreUrl', e.target.value)}
                        style={styles.input}
                        placeholder="https://play.google.com/store/apps/details?id=..."
                      />
                      <small style={styles.helpText}>Link to the app on Google Play Store</small>
                    </div>
                  </div>

                  {/* Update Message */}
                  <div style={styles.messageSection}>
                    <h3 style={styles.subsectionTitle}>Update Message</h3>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Update Message</label>
                      <textarea
                        value={settings[app.id]?.updateMessage || ''}
                        onChange={(e) => handleInputChange(app.id, 'updateMessage', e.target.value)}
                        style={styles.textarea}
                        rows={3}
                        placeholder="A new version of the app is available with important updates..."
                      />
                      <small style={styles.helpText}>Message shown to users when they need to update</small>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={styles.label}>Release Notes</label>
                      <textarea
                        value={settings[app.id]?.releaseNotes || ''}
                        onChange={(e) => handleInputChange(app.id, 'releaseNotes', e.target.value)}
                        style={styles.textarea}
                        rows={4}
                        placeholder="What's new in this version..."
                      />
                      <small style={styles.helpText}>Optional: What's new in this update (displayed on force update screen)</small>
                    </div>
                  </div>

                  {/* Last Updated Info */}
                  {settings[app.id]?.lastUpdated && (
                    <div style={styles.lastUpdated}>
                      Last updated: {new Date(settings[app.id].lastUpdated).toLocaleString()}
                    </div>
                  )}

                  {/* Warning Box */}
                  <div style={styles.warningBox}>
                    <strong>⚠️ Important:</strong>
                    <ul style={styles.warningList}>
                      <li>Setting a minimum version higher than what users have will immediately force them to update</li>
                      <li>Make sure the new version is available in both app stores before updating these settings</li>
                      <li>Test the update flow thoroughly before enabling force update</li>
                    </ul>
                  </div>

                  {/* Save Button */}
                  <div style={styles.saveButtonContainer}>
                    <button
                      onClick={() => handleSave(app.id)}
                      disabled={saving[app.id]}
                      style={{
                        ...styles.saveButton,
                        ...(saving[app.id] ? styles.saveButtonDisabled : {})
                      }}
                    >
                      {saving[app.id] ? 'Saving...' : `Save ${app.label} Settings`}
                    </button>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>

        {/* Help Section */}
        <div style={styles.helpSection}>
          <h3 style={styles.helpTitle}>How Force Update Works</h3>
          <div style={styles.helpGrid}>
            <div style={styles.helpCard}>
              <span style={styles.helpIcon}>📱</span>
              <h4>App Checks Version</h4>
              <p>When the app opens, it checks the current version against the minimum required version from the server.</p>
            </div>
            <div style={styles.helpCard}>
              <span style={styles.helpIcon}>🔒</span>
              <h4>Update Required</h4>
              <p>If the app version is below minimum, a blocking screen appears with the "Update Now" button.</p>
            </div>
            <div style={styles.helpCard}>
              <span style={styles.helpIcon}>🔄</span>
              <h4>Store Redirect</h4>
              <p>Clicking "Update Now" opens the appropriate app store (iOS or Android) for the user to update.</p>
            </div>
            <div style={styles.helpCard}>
              <span style={styles.helpIcon}>✅</span>
              <h4>Access Restored</h4>
              <p>After updating, the app works normally as the version now meets the minimum requirement.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  layout: {
    minHeight: '100vh',
    backgroundColor: '#F8FAFC'
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px'
  },
  header: {
    marginBottom: '30px'
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#0F172A',
    marginBottom: '8px'
  },
  subtitle: {
    fontSize: '1rem',
    color: '#64748B',
    margin: 0
  },
  loading: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#64748B',
    fontSize: '1.1rem'
  },
  message: {
    padding: '12px 20px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '0.95rem',
    fontWeight: '500'
  },
  messageSuccess: {
    backgroundColor: '#D1FAE5',
    color: '#065F46',
    border: '1px solid #A7F3D0'
  },
  messageError: {
    backgroundColor: '#FEE2E2',
    color: '#991B1B',
    border: '1px solid #FECACA'
  },
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    marginBottom: '30px'
  },
  tabContainer: {
    display: 'flex',
    borderBottom: '2px solid #E2E8F0',
    backgroundColor: '#F8FAFC'
  },
  tab: {
    flex: 1,
    padding: '16px 20px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: '500',
    color: '#64748B',
    borderBottom: '3px solid transparent',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  tabActive: {
    color: '#FF6961',
    borderBottomColor: '#FF6961',
    backgroundColor: 'white'
  },
  tabIcon: {
    fontSize: '1.3rem'
  },
  tabLabel: {
    fontSize: '0.95rem'
  },
  tabContent: {
    padding: '30px'
  },
  section: {
    maxWidth: '800px'
  },
  appHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  appInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  appIcon: {
    fontSize: '3rem'
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#0F172A',
    margin: 0
  },
  appDescription: {
    fontSize: '0.9rem',
    color: '#64748B',
    margin: '4px 0 0 0'
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center'
  },
  badge: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: '500'
  },
  badgeActive: {
    backgroundColor: '#D1FAE5',
    color: '#065F46'
  },
  badgeInactive: {
    backgroundColor: '#F1F5F9',
    color: '#64748B'
  },
  toggleSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#F8FAFC',
    borderRadius: '12px',
    marginBottom: '24px'
  },
  toggleInfo: {
    flex: 1
  },
  toggleDescription: {
    fontSize: '0.85rem',
    color: '#64748B',
    margin: '4px 0 0 0'
  },
  switch: {
    position: 'relative',
    display: 'inline-block',
    width: '50px',
    height: '28px'
  },
  slider: {
    position: 'absolute',
    cursor: 'pointer',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#CBD5E1',
    transition: '0.4s',
    borderRadius: '28px'
  },
  subsectionTitle: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: '16px'
  },
  versionSection: {
    marginBottom: '30px'
  },
  storeSection: {
    marginBottom: '30px'
  },
  messageSection: {
    marginBottom: '30px'
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: '8px'
  },
  platformIcon: {
    fontSize: '1.1rem'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.95rem',
    border: '1px solid #E2E8F0',
    borderRadius: '6px',
    outline: 'none',
    transition: 'border-color 0.2s',
    color: '#0F172A'
  },
  inputError: {
    borderColor: '#EF4444',
    backgroundColor: '#FEF2F2'
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.95rem',
    border: '1px solid #E2E8F0',
    borderRadius: '6px',
    outline: 'none',
    transition: 'border-color 0.2s',
    color: '#0F172A',
    fontFamily: 'inherit',
    resize: 'vertical'
  },
  helpText: {
    display: 'block',
    marginTop: '4px',
    fontSize: '0.85rem',
    color: '#64748B'
  },
  lastUpdated: {
    fontSize: '0.85rem',
    color: '#64748B',
    marginBottom: '20px',
    fontStyle: 'italic'
  },
  warningBox: {
    backgroundColor: '#FEF3C7',
    border: '1px solid #FCD34D',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '24px',
    color: '#92400E',
    fontSize: '0.9rem'
  },
  warningList: {
    marginTop: '8px',
    marginBottom: '0',
    paddingLeft: '20px'
  },
  saveButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  saveButton: {
    padding: '12px 32px',
    fontSize: '1rem',
    fontWeight: '600',
    color: 'white',
    backgroundColor: '#FF6961',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 2px 4px rgba(255, 105, 97, 0.2)'
  },
  saveButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },
  helpSection: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '30px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  },
  helpTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: '20px',
    textAlign: 'center'
  },
  helpGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '20px'
  },
  helpCard: {
    textAlign: 'center',
    padding: '20px',
    backgroundColor: '#F8FAFC',
    borderRadius: '8px'
  },
  helpIcon: {
    fontSize: '2rem',
    display: 'block',
    marginBottom: '12px'
  }
};

// (Removed dead toggle CSS: it was scoped to `.app-version-settings`, a class
// that was never applied to any element, and the slider color was set inline
// anyway. The toggle is now fully self-contained via state-driven inline styles.)

export default AppVersionSettings;
